/* eslint-disable no-nested-ternary */
import { useMutation, useQuery } from "@apollo/client";
import { PopoverPortal } from "@radix-ui/react-popover";
import DeleteRunScreenerInputMutation from "components/screener/v2/mutations/DeleteRunScreenerInputMutation";
import type {
  IDeleteRunScreenerInputMutation,
  IDeleteRunScreenerInputMutationVariables,
} from "components/screener/v2/mutations/__generated__/DeleteRunScreenerInputMutation.generated";
import RunScreenerInputsQuery from "components/screener/v2/query/RunScreenerInputsQuery";
import type {
  IRunScreenerInputsQuery,
  IRunScreenerInputsQueryVariables,
} from "components/screener/v2/query/__generated__/RunScreenerInputsQuery.generated";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { ScrollArea } from "components/ui/ScrollArea";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import range from "lodash/range";
import uniq from "lodash/uniq";
import { CircleCheckIcon, CircleChevronDownIcon, Trash2Icon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useHotkeys } from "react-hotkeys-hook";
import { FormattedMessage, useIntl } from "react-intl";
import type { IRunScreenerInput } from "types/graphqlTypes";
import { useMediaQuery } from "usehooks-ts";

type RunScreenerInput = Omit<IRunScreenerInputsQuery["runScreenerInputs"][number], "input"> & {
  input: IRunScreenerInput;
};
interface SavedScreenersProps {
  language: "pt-BR" | "en-US";
  activeObjectId?: string | null;
  disabled?: boolean;
  onSelect: (rsi: RunScreenerInput | null) => void;
}
export function SavedScreeners({ language, activeObjectId, disabled, onSelect }: SavedScreenersProps) {
  const intl = useIntl();
  const {
    data,
    loading,
    refetch: refetchRunScreenerInputs,
  } = useQuery<IRunScreenerInputsQuery, IRunScreenerInputsQueryVariables>(RunScreenerInputsQuery, {
    skip: disabled === true,
    variables: {
      language,
    },
  });
  const [deleteRunScreenerInput, { loading: isDeleting }] = useMutation<
    IDeleteRunScreenerInputMutation,
    IDeleteRunScreenerInputMutationVariables
  >(DeleteRunScreenerInputMutation);
  const [isOpen, setIsOpen] = useState(false);
  const [deletedRunScreenerInputs, setDeletedRunScreenerInputs] = useState<string[]>([]);

  let untitledIndex = 0;
  const runScreenerInputs = (data?.runScreenerInputs || [])
    .filter((node) => {
      return !deletedRunScreenerInputs.includes(node.objectId);
    })
    .map((node) => {
      untitledIndex += 1;
      return {
        ...node,
        title:
          node.title ||
          intl.formatMessage({ defaultMessage: "Sem título #{index}", id: "FUzwPj" }, { index: untitledIndex }),
      };
    })
    .sort((a, b) => {
      return a.title.localeCompare(b.title, undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });

  useHotkeys(
    ["ctrl+n", "meta+n", "alt+n"],
    () => {
      toast.success(intl.formatMessage({ defaultMessage: "Iniciando um novo screener...", id: "yIbHYr" }));
      onSelect(null);
    },
    {
      preventDefault: true,
      ignoreModifiers: false,
      enabled: isOpen === false,
    },
  );

  useEffect(() => {
    if (!isOpen) {
      refetchRunScreenerInputs();
    }
  }, [isOpen]);
  const pointRef = useRef({ x: 0, y: 0 });
  const virtualPopoverRef = useRef({
    getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...pointRef.current }),
  });
  const isMobile = useMediaQuery("(max-width: 639px)");

  return (
    <Popover modal={true} open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
      {isOpen && (
        <PopoverPortal>
          <div className="absolute bottom-0 left-0 right-0 top-0 z-10 bg-black/5 backdrop-blur-sm" />
        </PopoverPortal>
      )}
      {isOpen && isMobile ? (
        <PopoverAnchor virtualRef={virtualPopoverRef} />
      ) : (
        <PopoverTrigger asChild={true}>
          <button
            type="button"
            role="checkbox"
            disabled={disabled}
            aria-checked={isOpen ? "true" : "false"}
            className={classNames(
              "group",
              "relative isolate flex min-h-10 min-w-10 items-center justify-center rounded-xl border border-slate-300 bg-white p-2",
              "hover:bg-slate-100",
              "aria-checked:cursor-pointer aria-checked:border-violet-500/20 aria-checked:bg-violet-50",
              "hover:border-violet-300 hover:bg-violet-50",
              "focus:outline-none",
              { "z-20": isOpen },
              "disabled:pointer-events-none disabled:opacity-50",
              "dark:border-slate-950 dark:bg-slate-700",
              "dark:hover:bg-violet-400/30",
              "dark:aria-checked:bg-violet-400/30",
            )}
          >
            <CircleChevronDownIcon
              className={classNames(
                "fill-slate-50 stroke-slate-600",
                "group-hover:fill-violet-50 group-hover:stroke-violet-600",
                "group-aria-checked:fill-violet-50 group-aria-checked:stroke-violet-600",
                "dark:group-hover:fill-violet-400/20 dark:group-hover:stroke-violet-200",
                "dark:fill-slate-400/20 dark:stroke-slate-200",
                "dark:group-aria-checked:fill-transparent dark:group-aria-checked:stroke-violet-50",
                "size-4",
              )}
              strokeWidth={2.5}
              absoluteStrokeWidth={true}
            />
          </button>
        </PopoverTrigger>
      )}
      <PopoverContent
        className={classNames(
          "relative flex w-80 flex-col gap-y-2 rounded-2xl border border-zinc-300 py-2 shadow-md",
          "dark:border-slate-700",
          {
            "w-80 max-w-xs": !isMobile,
            "m-4 w-[calc(100vw-theme('spacing.8'))]": isMobile,
          },
        )}
        side={isMobile ? "top" : "bottom"}
        align={isMobile ? "start" : "start"}
        sideOffset={isMobile ? 0 : 4}
      >
        <div className="flex items-center gap-x-2 px-1">
          <button
            type="button"
            className="group flex size-8 items-center justify-center rounded-full bg-slate-100 p-1 hover:bg-slate-200 focus:outline-none dark:bg-slate-950"
            onClick={() => setIsOpen(false)}
          >
            <XIcon
              absoluteStrokeWidth={true}
              strokeWidth={3}
              className="size-5 stroke-slate-600 group-hover:stroke-slate-500 dark:stroke-slate-500 dark:group-hover:stroke-slate-300"
            />
          </button>
          <div>
            <h3 className="text-base font-bold text-slate-800 dark:text-white">
              <FormattedMessage defaultMessage="Screeners" id="Evc2/m" />
            </h3>
            <p className="text-xs font-bold tracking-wide text-slate-500 dark:text-sky-200">
              <FormattedMessage
                defaultMessage="{totalCount, plural, =1 {Apenas um} other {# salvos}}"
                id="dfb1bX"
                values={{
                  totalCount: runScreenerInputs.length,
                }}
              />
            </p>
          </div>
        </div>

        {runScreenerInputs.length > 0 && <Separator />}

        <ScrollArea tabIndex={0}>
          {loading && (
            <div className="flex flex-col gap-y-2 px-2 py-1" aria-hidden={true}>
              {range(0, 4).map((item) => {
                return (
                  <div
                    key={item}
                    className={classNames(
                      "h-5 w-full animate-pulse rounded-md bg-slate-200",
                      "dark:bg-slate-700 dark:ring-slate-700 dark:ring-offset-slate-700",
                      "flex items-center justify-center",
                    )}
                  />
                );
              })}
            </div>
          )}
          <ul
            className={classNames("flex max-h-96 select-none flex-col gap-y-1 px-1", "empty:hidden", {
              hidden: loading,
            })}
          >
            {runScreenerInputs.slice(0, 30).map((node) => {
              const isSelected = node.objectId === activeObjectId;
              return (
                <li
                  key={node.objectId}
                  className={classNames("group flex select-none items-center gap-x-1 pr-2", {
                    "pr-4": runScreenerInputs.length >= 12,
                  })}
                >
                  <button
                    type="button"
                    disabled={false}
                    className={classNames(
                      "group min-h-10 hover:font-bold hover:text-slate-600 lg:min-h-8",
                      "w-full rounded-md px-2 py-1 text-left text-sm font-medium",
                      "flex items-center justify-between",
                      "text-gray-600 hover:bg-slate-50",
                      "dark:font-bold dark:text-gray-300 dark:hover:bg-orange-400/20 dark:hover:text-orange-100",
                      {
                        "bg-blue-50 font-bold text-sea-600": isSelected,
                        "dark:bg-sky-400/20 dark:text-sky-200": isSelected,
                      },
                    )}
                    onClick={() => {
                      onSelect(node);
                      setIsOpen(false);
                    }}
                  >
                    <span className="line-clamp-2 max-w-52 text-ellipsis">{node.title}</span>

                    {isSelected && (
                      <CircleCheckIcon
                        aria-hidden={true}
                        className="size-4 fill-blue-100 stroke-sea-500 dark:fill-sky-400/30 dark:stroke-sky-200 dark:group-hover:fill-orange-400/10 dark:group-hover:stroke-orange-300"
                        absoluteStrokeWidth={true}
                        strokeWidth={2}
                      />
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={isDeleting || isSelected}
                    className="group"
                    title={intl.formatMessage({ defaultMessage: "Deletar", id: "XPy3Mz" })}
                    onClick={() => {
                      if (
                        // eslint-disable-next-line no-restricted-globals, no-alert
                        confirm(intl.formatMessage({ defaultMessage: "Deseja realmente deletar?", id: "lg+EZt" }))
                      ) {
                        setDeletedRunScreenerInputs(uniq([...deletedRunScreenerInputs, node.objectId]));
                        deleteRunScreenerInput({
                          variables: {
                            objectId: node.objectId,
                          },
                        });
                      }
                    }}
                  >
                    <Trash2Icon
                      strokeWidth={2}
                      absoluteStrokeWidth={true}
                      className={classNames(
                        "fill-slate-100 stroke-slate-600 transition-transform",
                        "dark:fill-red-400/10 dark:stroke-red-300",
                        "hover:scale-150 hover:fill-red-100 hover:stroke-red-600",
                        "group-aria-checked:fill-red-100 group-aria-checked:stroke-red-600",
                        "size-4",
                        "group-disabled:pointer-events-none group-disabled:opacity-50",
                      )}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
        <Separator />
        <div className="px-1">
          <button
            onClick={() => {
              onSelect(null);
              setIsOpen(false);
            }}
            type="button"
            className={classNames(
              "flex w-full items-center justify-center rounded-lg rounded-b-xl bg-slate-50 py-1.5 text-sm font-bold text-slate-600",
              "hover:bg-viridian-100 hover:text-viridian-700",
              "dark:bg-slate-700 dark:text-slate-300",
              "dark:hover:bg-viridian-400/30 dark:hover:text-viridian-200",
            )}
          >
            <FormattedMessage defaultMessage="Novo Screener" id="mRL9Tg" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
